import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { subscribeDeckMessages } from "@/lib/deckChannel";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { hydrateStore } from "./bootstrap";

// A writer broadcasts `deck-updated` on every persisted keystroke. A viewer that re-read the
// deck for each one would spend the whole session hydrating, so bursts collapse to one
// trailing read.
const FOLLOW_DEBOUNCE_MS = 250;

// Holds a skeleton until the store has been hydrated from IndexedDB, so the editor
// never flashes empty initial state before the persisted deck loads (TODO §4.4).
//
// Since §12 it is **keyed on `deckId` and re-gates when that changes** (§12.2.2): the open
// deck comes from the URL, and switching decks must not paint the previous deck's slides
// against the next deck's grid bodies. Callers additionally pass `key={deckId}` on the
// editor subtree so every piece of per-deck UI state — selection, panel, archive, scroll,
// the snapshot and thumbnail caches — is discarded by remount rather than unwound by hand
// (§12.2.3/§12.2.4).
export function HydrationGate({
  deckId,
  followExternalUpdates = false,
  children,
}: {
  deckId: string;
  // Re-read the deck when another page writes it (§12.5.3). True for a read-only viewer;
  // false for the writer, which is the one making those writes and must not clobber its own
  // in-flight state with a re-read.
  followExternalUpdates?: boolean;
  children: ReactNode;
}) {
  const [phase, setPhase] = useState<"loading" | "ready" | "missing">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    // hydrateStore never rejects (idb access is internally guarded), but catch() keeps the
    // gate from wedging on the skeleton if it ever did.
    hydrateStore(deckId)
      .catch(() => false)
      .then((found) => {
        if (!cancelled) setPhase(found ? "ready" : "missing");
      });
    return () => {
      cancelled = true;
    };
  }, [deckId]);

  // Cross-page reactions (§12.5.3). `deck-deleted` matters to every page — a writer whose
  // deck was deleted from the documents home must stop showing it, not keep writing to a
  // record that no longer exists.
  const followRef = useRef(followExternalUpdates);
  followRef.current = followExternalUpdates;
  useEffect(() => {
    let timer = 0;
    const unsubscribe = subscribeDeckMessages((msg) => {
      if (!("deckId" in msg) || msg.deckId !== deckId) return;
      if (msg.type === "deck-deleted") {
        window.clearTimeout(timer);
        setPhase("missing");
        return;
      }
      if (msg.type === "deck-updated" && followRef.current) {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          void hydrateStore(deckId);
        }, FOLLOW_DEBOUNCE_MS);
      }
    });
    return () => {
      window.clearTimeout(timer);
      unsubscribe();
    };
  }, [deckId]);

  if (phase === "loading") return <DeckSkeleton />;
  if (phase === "missing") return <DeckNotFound />;
  return <>{children}</>;
}

// §12.1.2 An unknown or deleted deck id. Deliberately NOT the bare `NotFound` route: the
// user did not mistype a URL, they followed a link to a document that is gone (or opened a
// deck another window has since deleted), and the useful next step is the documents home.
export function DeckNotFound() {
  return (
    <div className="app-screen-min flex items-center justify-center bg-slate-100 px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold text-slate-900">
          This deck isn’t here
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          It may have been deleted, or it lives in a different browser — decks
          are stored on the device that created them.
        </p>
        <Button asChild className="mt-6">
          <Link to="/documents">Go to your documents</Link>
        </Button>
      </div>
    </div>
  );
}

export function DeckSkeleton() {
  return (
    <div className="app-screen flex flex-col bg-slate-100">
      <div className="flex items-center justify-between gap-6 bg-slate-900 px-6 py-3">
        <Skeleton className="h-8 w-32 bg-white/10" />
        <Skeleton className="h-8 w-40 bg-white/10" />
      </div>
      <div className="flex flex-1 min-h-0">
        <aside className="hidden h-full w-1/5 min-w-[200px] shrink-0 border-r border-slate-200 bg-white p-4 lg:block">
          <Skeleton className="mb-4 h-5 w-20" />
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
          </div>
        </aside>
        <div className="flex flex-1 flex-col items-center overflow-hidden px-6 py-8">
          <Skeleton className="aspect-[16/9] w-full max-w-5xl rounded-md" />
        </div>
      </div>
    </div>
  );
}
